import React, { useState, useEffect, useMemo } from 'react';
import Modal from 'react-modal';

import BulkCreateHoldModal from './components/hold/BulkCreateHoldModal';

Modal.setAppElement('#root');

const initialHoldFormState = {
    deltagere: '',
    prøve: '',
    låst: false,
    sprogcenter_id: '',
    skriftlig_censor_id: '', 
    mundtlig_censor_id: ''  
};

const getPrøveForkortelseFraFuldtNavn = (fuldtNavn) => {
    if (!fuldtNavn) return '';
    const lowerCaseNavn = fuldtNavn.toLowerCase();
    if (lowerCaseNavn.includes('medborgerskab')) return 'MBP';
    if (lowerCaseNavn.includes('indfødsret')) return 'IFP';
    if (lowerCaseNavn === 'pd1') return 'PD1';
    if (lowerCaseNavn === 'pd2') return 'PD2';
    if (lowerCaseNavn === 'pd3') return 'PD3';
    return fuldtNavn;
};

function HoldListe() {
  const [terminer, setTerminer] = useState([]); 
  const[holdForValgtTermin, setHoldForValgtTermin] = useState([]);
  const[loadingTerminer, setLoadingTerminer] = useState(true);
  const [loadingHold, setLoadingHold] = useState(false);
  const [error, setError] = useState(null);
  
  const[holdModalIsOpen, setHoldModalIsOpen] = useState(false);
  const [holdFormState, setHoldFormState] = useState(initialHoldFormState);
  const [modalError, setModalError] = useState(null);
  const[editingHoldId, setEditingHoldId] = useState(null);
  
  const [terminModalIsOpen, setTerminModalIsOpen] = useState(false);
  const [nyTerminNavn, setNyTerminNavn] = useState('');
  const[terminModalError, setTerminModalError] = useState(null);
  const [bulkModalIsOpen, setBulkModalIsOpen] = useState(false);
  
  const [sprogcentreOptions, setSprogcentreOptions] = useState([]);
  const[censorerOptions, setCensorerOptions] = useState([]);
  const[loadingModalOptions, setLoadingModalOptions] = useState(true);
  const [activeTermin, setActiveTermin] = useState(null); 
  const [valgtPrøvetype, setValgtPrøvetype] = useState('Skriftlig');
  const [sortConfig, setSortConfig] = useState({ primaryKey: 'sprogcenter_navn', secondaryKey: null, direction: 'ascending' });
  const [isDistributing, setIsDistributing] = useState(false);
  const [distributionMessage, setDistributionMessage] = useState('');
  const [allocationLog, setAllocationLog] = useState(null);
  const [allocationLogModalOpen, setAllocationLogModalOpen] = useState(false);
  
  const[sprogcenterForModal, setSprogcenterForModal] = useState(null);

  const API_BASE_URL_HOLD = `${process.env.REACT_APP_API_URL}/api/hold`;
  const API_BASE_URL_SPROGCENTRE = `${process.env.REACT_APP_API_URL}/api/sprogcentre`;
  const API_BASE_URL_CENSORER = `${process.env.REACT_APP_API_URL}/api/censorer`;
  const API_BASE_URL_TERMINER = `${process.env.REACT_APP_API_URL}/api/terminer`;

  const openHoldModal = (holdToEdit = null) => {
    setError(null);
    setModalError(null);
    if (holdToEdit) {
      setEditingHoldId(holdToEdit.id);
      setHoldFormState({
        deltagere: holdToEdit.deltagere || '',
        prøve: getPrøveForkortelseFraFuldtNavn(holdToEdit.prøve),
        låst: holdToEdit.låst || false,
        sprogcenter_id: holdToEdit.sprogcenter ? holdToEdit.sprogcenter.id.toString() : '',
        skriftlig_censor_id: holdToEdit.skriftlig_censor ? holdToEdit.skriftlig_censor.id.toString() : '', 
        mundtlig_censor_id: holdToEdit.mundtlig_censor ? holdToEdit.mundtlig_censor.id.toString() : '',   
      });
    } else {
      setEditingHoldId(null);
      setHoldFormState(initialHoldFormState);
    }
    setHoldModalIsOpen(true);
  };

  const closeHoldModal = () => { 
    setHoldModalIsOpen(false); 
    setModalError(null); 
    setEditingHoldId(null);
  };

  const openBulkModalForSprogcenter = (sprogcenter) => {
    setSprogcenterForModal(sprogcenter);
    setBulkModalIsOpen(true);
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      setLoadingTerminer(true); 
      setLoadingModalOptions(true); 
      setError(null);
      try {
        const fetchOptions = { credentials: 'include' };
        const[terminerRes, sprogcentreRes, censorerRes] = await Promise.all([
          fetch(API_BASE_URL_TERMINER, fetchOptions),
          fetch(API_BASE_URL_SPROGCENTRE, fetchOptions),
          fetch(API_BASE_URL_CENSORER, fetchOptions),
        ]);
        if (!terminerRes.ok) throw new Error(`Terminer: ${terminerRes.statusText}`);
        if (!sprogcentreRes.ok) throw new Error(`Sprogcentre: ${sprogcentreRes.statusText}`);
        if (!censorerRes.ok) throw new Error(`Censorer: ${censorerRes.statusText}`);
        
        const terminerData = await terminerRes.json();
        const sprogcentreData = await sprogcentreRes.json();
        const censorerData = await censorerRes.json();
        
        setTerminer(terminerData); 
        setSprogcentreOptions(sprogcentreData); 
        setCensorerOptions(censorerData);
      } catch (err) { 
        console.error("Fejl ved hentning af initial data:", err); 
        setError(err.message);
      } finally { 
        setLoadingTerminer(false); 
        setLoadingModalOptions(false); 
      }
    };

    fetchInitialData();
  },[API_BASE_URL_TERMINER, API_BASE_URL_SPROGCENTRE, API_BASE_URL_CENSORER]);

  useEffect(() => {
    if (activeTermin && activeTermin.id) {
        setLoadingHold(true);
        setError(null);
        setDistributionMessage('');
        
        const url = `${API_BASE_URL_HOLD}?terminId=${encodeURIComponent(activeTermin.id)}`;
        
        fetch(url, { credentials: 'include' })
            .then(res => {
                if (!res.ok) {
                    throw new Error(`Netværksfejl ved hentning af hold: ${res.statusText}`);
                }
                return res.json();
            })
            .then(data => {
                setHoldForValgtTermin(data);
            })
            .catch(err => {
                console.error(`Fejl ved hentning af hold for termin ${activeTermin.navn}:`, err);
                setError(err.message);
                setHoldForValgtTermin([]);
            })
            .finally(() => {
                setLoadingHold(false);
            });
    } else {
        setHoldForValgtTermin([]);
    }
  },[activeTermin, API_BASE_URL_HOLD]);

  const handleTilbageTilTerminer = () => {
    setActiveTermin(null);
    setHoldForValgtTermin([]);
    setValgtPrøvetype('Skriftlig'); 
    setError(null);
    setDistributionMessage('');
  };

  const handleHoldFormInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    setHoldFormState(prevState => {
      const newState = { ...prevState, [name]: type === 'checkbox' ? checked : value };
      if (name === 'prøve') { newState.sprogcenter_id = ''; }
      return newState;
    });
  };

  const handleHoldFormSubmit = async (event) => {
    event.preventDefault(); 
    setModalError(null);

    const deltagereInt = parseInt(holdFormState.deltagere, 10);
    if (isNaN(deltagereInt) || deltagereInt < 0) { 
      setModalError('Antal deltagere skal være et gyldigt, positivt tal.'); 
      return; 
    }
    
    if (!activeTermin?.id) { 
      setModalError('Fejl: Ingen aktiv termin er valgt.'); 
      return; 
    }
    if (!holdFormState.prøve) { 
      setModalError('Prøve skal vælges.'); 
      return; 
    }

    const holdData = {
      deltagere: deltagereInt,
      termin_id: activeTermin.id,
      prøve: holdFormState.prøve,
      låst: holdFormState.låst,
      sprogcenter_id: holdFormState.sprogcenter_id ? parseInt(holdFormState.sprogcenter_id, 10) : null,
      skriftlig_censor_id: holdFormState.skriftlig_censor_id ? parseInt(holdFormState.skriftlig_censor_id, 10) : null,
      mundtlig_censor_id: holdFormState.mundtlig_censor_id ? parseInt(holdFormState.mundtlig_censor_id, 10) : null,
    };
    
    if (isNaN(holdData.sprogcenter_id)) holdData.sprogcenter_id = null;
    if (isNaN(holdData.skriftlig_censor_id)) holdData.skriftlig_censor_id = null;
    if (isNaN(holdData.mundtlig_censor_id)) holdData.mundtlig_censor_id = null;

    const submitButton = event.target.querySelector('button[type="submit"]');
    if(submitButton) submitButton.disabled = true;

    try {
      let response;
      const fetchOptions = {
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(holdData),
        credentials: 'include'
      };

      if (editingHoldId) {
        response = await fetch(`${API_BASE_URL_HOLD}/${editingHoldId}`, { method: 'PUT', ...fetchOptions });
      } else {
        response = await fetch(API_BASE_URL_HOLD, { method: 'POST', ...fetchOptions });
      }

      if (!response.ok) { 
        const errorText = await response.text(); 
        throw new Error(errorText || `Serverfejl: ${response.status}`); 
      }
      
      closeHoldModal();
      
      const url = `${API_BASE_URL_HOLD}?terminId=${encodeURIComponent(activeTermin.id)}`;
      const getResponse = await fetch(url, { credentials: 'include' });
      if (!getResponse.ok) throw new Error(`Netværksfejl ved opdatering af liste: ${getResponse.statusText}`);
      const data = await getResponse.json();
      setHoldForValgtTermin(data);

    } catch (error) { 
      console.error(editingHoldId ? "Fejl ved opdatering:" : "Fejl ved gemning:", error); 
      setModalError(`Kunne ikke ${editingHoldId ? 'opdatere' : 'gemme'} hold: ${error.message}`);
    } finally { 
      if(submitButton) submitButton.disabled = false; 
    }
  };
  
  const handleTerminSubmit = async (event) => {
        event.preventDefault();
        setTerminModalError(null);
        if (!nyTerminNavn.trim()) {
            setTerminModalError('Navn må ikke være tomt.');
            return;
        }
        try {
            const response = await fetch(API_BASE_URL_TERMINER, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ navn: nyTerminNavn.trim() }),
                credentials: 'include'
            });
            if (!response.ok) { const errorText = await response.text(); throw new Error(errorText); }
            const nyTermin = await response.json();
            setTerminer(prev => [...prev, nyTermin].sort((a,b) => b.navn.localeCompare(a.navn)));
            setNyTerminNavn('');
            setTerminModalIsOpen(false);
        } catch(err) {
            console.error("Fejl ved oprettelse af termin:", err);
            setTerminModalError(err.message);
        }
  };
  
  const handleBulkCreateSuccess = async (oprettetTilTerminId) => {
    const terminerRes = await fetch(API_BASE_URL_TERMINER, { credentials: 'include' });
    const terminerData = await terminerRes.json();
    setTerminer(terminerData);
    
    const opdateretTerminObj = terminerData.find(t => t.id === oprettetTilTerminId);
    if (opdateretTerminObj) {
      setActiveTermin(opdateretTerminObj);
      const url = `${API_BASE_URL_HOLD}?terminId=${encodeURIComponent(opdateretTerminObj.id)}`;
      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) {
          const data = await response.json();
          setHoldForValgtTermin(data);
      }
    }
  };

  const handleDeleteHold = async (idToDelete) => {
    if (!window.confirm(`Slet hold ID ${idToDelete}?`)) return;
    try {
      const response = await fetch(`${API_BASE_URL_HOLD}/${idToDelete}`, { method: 'DELETE', credentials: 'include' });
      if (!response.ok && response.status !== 204) {
        const errorText = await response.text();
        throw new Error(errorText || `Serverfejl: ${response.status}`);
      }
      
      const url = `${API_BASE_URL_HOLD}?terminId=${encodeURIComponent(activeTermin.id)}`;
      const getResponse = await fetch(url, { credentials: 'include' });
      if (getResponse.ok) {
          const data = await getResponse.json();
          setHoldForValgtTermin(data);
      }
    } catch(err) {
      console.error("Fejl ved sletning af hold", err);
      setError(`Kunne ikke slette hold: ${err.message}`);
    }
  };
  
  const handleToggleLock = async (holdId) => {
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL_HOLD}/${holdId}/togglelock`, { method: 'PATCH', credentials: 'include' });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `Serverfejl: ${response.status} ved lås/aflås`);
      }
      const updatedHoldFromServer = await response.json();
      setHoldForValgtTermin(prevHold => prevHold.map(h => h.id === holdId ? updatedHoldFromServer : h));
    } catch (error) {
      console.error("Fejl opdatering af lås:", error);
      setError(`Kunne ikke opdatere lås: ${error.message}`);
    }
  };
  
  const handleFordelCensorer = async () => {
    if (!activeTermin || !activeTermin.id) {
      alert("Vælg venligst en termin fra listen først.");
      return;
    }
    if (!window.confirm(`Fordel censorer for termin: ${activeTermin.navn}?`)) return;
    setIsDistributing(true);
    setError(null);
    setDistributionMessage('');
    try {
      const response = await fetch(`${API_BASE_URL_HOLD}/fordel-censorer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ termin_id: activeTermin.id }),
        credentials: 'include'
      });
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.message || `Serverfejl: ${response.status}`);
      }
      setDistributionMessage(result.message);
      if (result.log) {
        setAllocationLog(result.log);
        setAllocationLogModalOpen(true);
      }
      
      const url = `${API_BASE_URL_HOLD}?terminId=${encodeURIComponent(activeTermin.id)}`;
      const getResponse = await fetch(url, { credentials: 'include' });
      if (getResponse.ok) {
          const data = await getResponse.json();
          setHoldForValgtTermin(data);
      }
    } catch (error) {
      console.error("Fejl fordeling:", error);
      setError(`Fordelingsfejl: ${error.message}`);
      setDistributionMessage(`Fordelingsfejl: ${error.message}`);
    } finally {
      setIsDistributing(false);
    }
  };
  
  const toggleSprogcenterExpand = (sprogcenterId) => {
    setExpandedSprogcentre(prevExpanded => {
      if (prevExpanded.includes(sprogcenterId)) {
        return prevExpanded.filter(id => id !== sprogcenterId);
      } else {
        return [...prevExpanded, sprogcenterId];
      }
    });
  };

  const [expandedSprogcentre, setExpandedSprogcentre] = useState([]); 

  const groupedAndSortedHold = useMemo(() => {
    const grouped = sprogcentreOptions.reduce((acc, sprogcenter) => {
      acc[sprogcenter.navn] = { id: sprogcenter.id, hold:[] };
      return acc;
    }, {});

    holdForValgtTermin.forEach(hold => {
      const key = hold.sprogcenter ? hold.sprogcenter.navn : 'Ikke Tildelt';
      if (!grouped[key]) {
        grouped[key] = { id: 'n-a', hold:[] };
      }
      grouped[key].hold.push(hold);
    });

    Object.keys(grouped).forEach(key => {
        const holdIGruppe = grouped[key].hold;
        grouped[key].antalHold = holdIGruppe.length;
        grouped[key].totalDeltagere = holdIGruppe.reduce((sum, currentHold) => sum + currentHold.deltagere, 0);

        grouped[key].deltagerePD1 = holdIGruppe.filter(h => h.prøve === 'PD1').reduce((sum, h) => sum + h.deltagere, 0);
        grouped[key].deltagerePD2 = holdIGruppe.filter(h => h.prøve === 'PD2').reduce((sum, h) => sum + h.deltagere, 0);
        grouped[key].deltagerePD3 = holdIGruppe.filter(h => h.prøve === 'PD3').reduce((sum, h) => sum + h.deltagere, 0);
        grouped[key].deltagereSP = holdIGruppe.filter(h => h.prøve === 'SP').reduce((sum, h) => sum + h.deltagere, 0);
    });

    const sortedGroupKeys = Object.keys(grouped).sort((a, b) => a.localeCompare(b));

    if (sortConfig.secondaryKey) {
      const { secondaryKey, direction } = sortConfig;
      const isAsc = direction === 'ascending';

      sortedGroupKeys.forEach(key => {
        grouped[key].hold.sort((a, b) => {
          let valA, valB;
          
          switch (secondaryKey) {
            case 'skriftlig_censor_navn':
              valA = a.skriftlig_censor ? `${a.skriftlig_censor.fornavn} ${a.skriftlig_censor.efternavn}`.toLowerCase() : '';
              valB = b.skriftlig_censor ? `${b.skriftlig_censor.fornavn} ${b.skriftlig_censor.efternavn}`.toLowerCase() : '';
              break;
            case 'mundtlig_censor_navn':
              valA = a.mundtlig_censor ? `${a.mundtlig_censor.fornavn} ${a.mundtlig_censor.efternavn}`.toLowerCase() : '';
              valB = b.mundtlig_censor ? `${b.mundtlig_censor.fornavn} ${b.mundtlig_censor.efternavn}`.toLowerCase() : '';
              break;
            case 'prøve':
              valA = a[secondaryKey] ? a[secondaryKey].toLowerCase() : '';
              valB = b[secondaryKey] ? b[secondaryKey].toLowerCase() : '';
              break;
            case 'deltagere':
            case 'låst':
              valA = a[secondaryKey];
              valB = b[secondaryKey];
              break;
            default:
              return 0; 
          }

          if (valA === null || valA === undefined || valA === '') return isAsc ? 1 : -1;
          if (valB === null || valB === undefined || valB === '') return isAsc ? -1 : 1;
          
          if (valA < valB) return isAsc ? -1 : 1;
          if (valA > valB) return isAsc ? 1 : -1;
          
          return 0;
        });
      });
    }
    
    return { sortedGroupKeys, groupedData: grouped };
  }, [holdForValgtTermin, sprogcentreOptions, sortConfig]);

  const requestSort = (key) => {
    if (key === 'sprogcenter_navn') return;
    let direction = 'ascending';
    if (sortConfig.secondaryKey === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ primaryKey: 'sprogcenter_navn', secondaryKey: key, direction: direction });
  };

  const getSortIndicatorClass = (name) => {
    if (!sortConfig || sortConfig.secondaryKey !== name) {
      return 'sortable';
    }
    return sortConfig.direction === 'ascending' ? 'sorted-asc' : 'sorted-desc';
  };

  const filteredSprogcentreOptions = useMemo(() => {
    if (!holdFormState.prøve || sprogcentreOptions.length === 0) return sprogcentreOptions;
    const prøveKeyMap = { 'PD1': 'udbyder_pd1', 'PD2': 'udbyder_pd2', 'PD3': 'udbyder_pd3', 'SP': 'udbyder_sp' };
    const udbyderKey = prøveKeyMap[holdFormState.prøve];
    if (!udbyderKey) return sprogcentreOptions;
    return sprogcentreOptions.filter(sc => sc[udbyderKey] === true);
  }, [holdFormState.prøve, sprogcentreOptions]);

  const handleToggleTerminLock = async () => {
    if (!activeTermin) return;
    
    const action = activeTermin.er_afsluttet ? "genåbne" : "afslutte og låse";
    if (!window.confirm(`Er du sikker på, at du vil ${action} terminen "${activeTermin.navn}"?`)) {
        return;
    }

    try {
      const response = await fetch(`${API_BASE_URL_TERMINER}/${activeTermin.id}/toggle-lock`, {
        method: 'PATCH',
        credentials: 'include'
      });

      if (!response.ok) throw new Error(`Fejl: ${response.statusText}`);
      
      const opdateretTermin = await response.json();
      setActiveTermin(opdateretTermin);
      
      setTerminer(prevTerminer => prevTerminer.map(t => 
          t.id === opdateretTermin.id ? opdateretTermin : t
      ));

    } catch (err) {
      console.error("Kunne ikke ændre termin status:", err);
      alert("Der skete en fejl: " + err.message);
    }
  };

  if (loadingTerminer) {
    return <p className="info-message">Henter data...</p>;
  }
  if (error && !activeTermin) {
    return <p className="error-message" style={{ textAlign: 'center', marginTop: '20px' }}>{error}</p>;
  }

  return (
    <div>
      {!activeTermin && (
        <div className="add-new-button-container">
          <button onClick={() => setTerminModalIsOpen(true)} className="toggle-form-button primary-action">Tilføj Ny Termin</button>
        </div>
      )}
      
      <h2 className="page-main-header"> 
        {activeTermin ? `Hold for ${activeTermin.navn}` : 'Eksisterende Terminer'}
      </h2>
      
      {!activeTermin ? (
        <div className="table-container" style={{marginTop: '20px'}}>
            <table className="censor-table termin-clickable-table">
              <thead>
                <tr>
                  <th>Termin Navn</th>
                  <th style={{width: '200px', textAlign: 'center'}}>Handlinger</th>
                </tr>
              </thead>
              <tbody>
                {terminer.map(terminItem => (
                  <tr 
                    key={terminItem.id} 
                    onClick={() => setActiveTermin(terminItem)} 
                    className="clickable-row" 
                    title={`Vis hold for ${terminItem.navn}`}
                  >
                    <td>
                      <strong>{terminItem.navn}</strong>
                      {terminItem.er_afsluttet && <span style={{marginLeft: '10px', color: '#e74c3c'}}>&#128274; (Afsluttet)</span>}
                    </td>
                    <td style={{textAlign: 'center'}}>
                      <button 
                        className="view-button small-button" 
                        onClick={(e) => { 
                            e.stopPropagation(); 
                            setActiveTermin(terminItem); 
                        }}
                      >
                        Se Hold for Termin
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
        </div>
      ) : (
        <div> 
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px'}}>
              <button onClick={handleTilbageTilTerminer} className="back-button" style={{marginBottom: 0}}>← Tilbage til Terminer</button>
              
              <button 
                onClick={handleToggleTerminLock} 
                className={`toggle-form-button ${activeTermin.er_afsluttet ? 'unlock-btn' : 'lock-btn'}`}
                style={{backgroundColor: activeTermin.er_afsluttet ? '#f39c12' : '#2c3e50'}}
              >
                {activeTermin.er_afsluttet ? (
                    <>&#128275; Genåbn Termin</>
                ) : (
                    <>&#128274; Afslut & Lås Termin</>
                )}
              </button>
          </div>

          <div className="distribute-button-container">
            <button onClick={handleFordelCensorer} className="toggle-form-button" disabled={isDistributing || loadingHold || holdForValgtTermin.length === 0 || activeTermin.er_afsluttet}>
              {isDistributing ? 'Fordeler...' : `Fordel Censorer for ${activeTermin.navn}`}
            </button>
            {allocationLog && !isDistributing && (
              <button
                onClick={() => setAllocationLogModalOpen(true)}
                className="secondary-button"
                style={{ marginLeft: '10px' }}
              >
                Vis fordelingslog
              </button>
            )}
          </div>
          {distributionMessage && <p className={`info-message ${error && activeTermin && !isDistributing ? 'error-message-inline' : 'success-message-inline'}`}>{distributionMessage}</p>}
          {error && activeTermin && !loadingHold && !isDistributing && <p className="error-message" style={{ textAlign: 'center', marginBottom: '15px' }}>{error}</p>}

          <div className="view-toggle-container">
            <button className={`toggle-button ${valgtPrøvetype === 'Skriftlig' ? 'active' : ''}`} onClick={() => setValgtPrøvetype('Skriftlig')}>
              Skriftlige Hold
            </button>
            <button className={`toggle-button ${valgtPrøvetype === 'Mundtlig' ? 'active' : ''}`} onClick={() => setValgtPrøvetype('Mundtlig')}>
              Mundtlige Hold
            </button>
          </div>

          {loadingHold && <p className="info-message">Henter hold...</p>}
          {!loadingHold && (
            <>
              {valgtPrøvetype === 'Skriftlig' && (
                  <div className="table-container">
                    <table className="censor-table">
                      <thead>
                        <tr>
                          <th onClick={() => requestSort('prøve')} className={getSortIndicatorClass('prøve')}>Prøve</th>
                          <th onClick={() => requestSort('deltagere')} className={getSortIndicatorClass('deltagere')}>Delt.</th>
                          <th onClick={() => requestSort('låst')} className={getSortIndicatorClass('låst')}>Låst</th>
                          <th onClick={() => requestSort('skriftlig_censor_navn')} className={getSortIndicatorClass('skriftlig_censor_navn')}>Skriftlig Censor</th>
                          <th className="action-cell">Handlinger</th>
                        </tr>
                      </thead>
                      <tbody>
                        {groupedAndSortedHold.sortedGroupKeys.map(sprogcenterNavn => {
                          const gruppe = groupedAndSortedHold.groupedData[sprogcenterNavn];
                          const sprogcenterId = gruppe.id;
                          const sprogcenterObj = sprogcentreOptions.find(sc => sc.id === sprogcenterId);
                          const erExpanded = expandedSprogcentre.includes(sprogcenterId);

                          return (
                            <React.Fragment key={`skriftlig-group-${sprogcenterNavn}`}>
                              <tr className="sprogcenter-group-header clickable-row" onClick={() => toggleSprogcenterExpand(sprogcenterId)}>
                                <td colSpan="6">
                                  <div className="group-header-content">
                                    <span className="accordion-info">
                                      <span className={`accordion-arrow ${erExpanded ? 'expanded' : ''}`}>&#9654;</span>
                                      <strong>{sprogcenterNavn}</strong>
                                    </span>
                                    <span className="group-header-stats">
                                      {gruppe.deltagerePD1 > 0 && <span>PD1: {gruppe.deltagerePD1}</span>}
                                      {gruppe.deltagerePD2 > 0 && <span>PD2: {gruppe.deltagerePD2}</span>}
                                      {gruppe.deltagerePD3 > 0 && <span>PD3: {gruppe.deltagerePD3}</span>}
                                      {gruppe.deltagereSP > 0 && <span>SP: {gruppe.deltagereSP}</span>}
                                      <span className="stat-total">Hold: {gruppe.antalHold}</span>
                                      <span className="stat-total">Total Deltagere: {gruppe.totalDeltagere}</span>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openBulkModalForSprogcenter(sprogcenterObj);
                                        }}
                                        className="add-hold-to-group-button"
                                        title={`Tilføj hold til ${sprogcenterNavn}`}
                                        disabled={!sprogcenterObj || activeTermin.er_afsluttet}
                                      >
                                        + Tilføj Hold
                                      </button>
                                    </span>
                                  </div>
                                </td>
                              </tr>
                              {erExpanded && gruppe.hold.map(h => (
                                <tr key={`skriftlig-${h.id}`} className={h.låst ? 'row-locked' : ''}>
                                  <td>{h.prøve}</td>
                                  <td>{h.deltagere}</td>
                                  <td className={h.låst ? 'status-locked' : 'status-unlocked'}>{h.låst ? <strong>Ja</strong> : 'Nej'}</td>
                                  <td>
                                    {h.skriftlig_censor ? `${h.skriftlig_censor.fornavn} ${h.skriftlig_censor.efternavn}` : 'N/A'}
                                  </td>
                                  <td className="action-cell">
                                    <a href={`${process.env.REACT_APP_API_URL}/api/hold/${h.id}/pdf`} className="pdf-button" title={`Generer PDF for hold ${h.id}`} target="_blank" rel="noopener noreferrer">PDF</a>
                                    <button onClick={() => openHoldModal(h)} className="edit-button icon-button" title={`Rediger hold ${h.id}`} disabled={activeTermin.er_afsluttet}><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M21.731 2.269a2.625 2.625 0 00-3.712 0l-1.157 1.157 3.712 3.712 1.157-1.157a2.625 2.625 0 000-3.712zM19.513 8.199l-3.712-3.712-8.4 8.4a5.25 5.25 0 00-1.32 2.214l-.8 2.685a.75.75 0 00.933.933l2.685-.8a5.25 5.25 0 002.214-1.32l8.4-8.4z" /><path d="M5.25 5.25a3 3 0 00-3 3v10.5a3 3 0 003 3h10.5a3 3 0 003-3V13.5a.75.75 0 00-1.5 0v5.25a1.5 1.5 0 01-1.5 1.5H5.25a1.5 1.5 0 01-1.5-1.5V8.25a1.5 1.5 0 011.5-1.5h5.25a.75.75 0 000-1.5H5.25z" /></svg></button>
                                    <button onClick={() => handleToggleLock(h.id)} className="icon-button" title={h.låst ? `Aflås` : `Lås`} disabled={activeTermin.er_afsluttet}>{h.låst ? (<svg viewBox="0 0 24 24" fill="currentColor" className="lock-icon locked"><path fillRule="evenodd" d="M12 1.5a5.25 5.25 0 00-5.25 5.25v3a3 3 0 00-3 3v6.75a3 3 0 003 3h10.5a3 3 0 003-3v-6.75a3 3 0 00-3-3v-3A5.25 5.25 0 0012 1.5zm-3.75 5.25v3h7.5v-3a3.75 3.75 0 00-7.5 0z" clipRule="evenodd" /></svg>) : (<svg viewBox="0 0 24 24" fill="currentColor" className="lock-icon unlocked"><path d="M18 1.5c2.9 0 5.25 2.35 5.25 5.25v3.75a.75.75 0 01-1.5 0V6.75A3.75 3.75 0 0018 3a3.75 3.75 0 00-3.75 3.75V9.75a.75.75 0 01-1.5 0V6.75C12.75 3.85 15.1 1.5 18 1.5z" /><path fillRule="evenodd" d="M6 6.75V9.75a.75.75 0 01-1.5 0V6.75a.75.75 0 01.75-.75H5.5a5.251 5.251 0 014.66-4.993.75.75 0 11.338 1.46A3.75 3.75 0 005.5 6h-.75a.75.75 0 01-.75.75zM4.5 9.75A3 3 0 001.5 12.75v6.75a3 3 0 003 3h10.5a3 3 0 003-3v-6.75a3 3 0 00-3-3H4.5z" clipRule="evenodd" /></svg>)}</button>
                                    <button onClick={() => handleDeleteHold(h.id)} className="delete-button icon-button" title={`Slet`} disabled={activeTermin.er_afsluttet}><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="delete-icon"><path fillRule="evenodd" d="M16.5 4.478v.227a48.816 48.816 0 013.878.512.75.75 0 11-.256 1.478l-.209-.035-1.005 13.07a3 3 0 01-2.991 2.77H8.084a3 3 0 01-2.991-2.77L4.087 6.66l-.209.035a.75.75 0 01-.256-1.478A48.567 48.567 0 017.5 4.705v-.227c0-1.564 1.213-2.9 2.816-2.9h1.368c1.603 0 2.816 1.336 2.816 2.9zm-1.452.721a.75.75 0 01.216-.534l.018-.018a.75.75 0 01.706-.216L17.5 5.25a.75.75 0 01.216.534l-.018.018a.75.75 0 01-.706.216l-1.281-.214a.75.75 0 01-.216-.534z" clipRule="evenodd" /></svg></button>
                                  </td>
                                </tr>
                              ))}
                              {erExpanded && gruppe.hold.length === 0 && (
                                <tr>
                                  <td colSpan="6" className="no-hold-in-group-message">Ingen hold oprettet for dette sprogcenter.</td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
              )}

              {valgtPrøvetype === 'Mundtlig' && (
                  <div className="table-container">
                    <table className="censor-table">
                      <thead>
                        <tr>
                          <th onClick={() => requestSort('prøve')} className={getSortIndicatorClass('prøve')}>Prøve</th>
                          <th onClick={() => requestSort('deltagere')} className={getSortIndicatorClass('deltagere')}>Delt.</th>
                          <th onClick={() => requestSort('låst')} className={getSortIndicatorClass('låst')}>Låst</th>
                          <th onClick={() => requestSort('mundtlig_censor_navn')} className={getSortIndicatorClass('mundtlig_censor_navn')}>Mundtlig Censor</th>
                          <th className="action-cell">Handlinger</th>
                        </tr>
                      </thead>
                      <tbody>
                        {groupedAndSortedHold.sortedGroupKeys.map(sprogcenterNavn => {
                          const gruppe = groupedAndSortedHold.groupedData[sprogcenterNavn];
                          const sprogcenterId = gruppe.id;
                          const sprogcenterObj = sprogcentreOptions.find(sc => sc.id === sprogcenterId);
                          const erExpanded = expandedSprogcentre.includes(sprogcenterId);

                          return (
                            <React.Fragment key={`mundtlig-group-${sprogcenterNavn}`}>
                              <tr className="sprogcenter-group-header clickable-row" onClick={() => toggleSprogcenterExpand(sprogcenterId)}>
                                <td colSpan="6">
                                  <div className="group-header-content">
                                    <span className="accordion-info">
                                      <span className={`accordion-arrow ${erExpanded ? 'expanded' : ''}`}>&#9654;</span>
                                      <strong>{sprogcenterNavn}</strong>
                                    </span>
                                    <span className="group-header-stats">
                                      {gruppe.deltagerePD1 > 0 && <span>PD1: {gruppe.deltagerePD1}</span>}
                                      {gruppe.deltagerePD2 > 0 && <span>PD2: {gruppe.deltagerePD2}</span>}
                                      {gruppe.deltagerePD3 > 0 && <span>PD3: {gruppe.deltagerePD3}</span>}
                                      {gruppe.deltagereSP > 0 && <span>SP: {gruppe.deltagereSP}</span>}
                                      <span className="stat-total">Hold: {gruppe.antalHold}</span>
                                      <span className="stat-total">Total Deltagere: {gruppe.totalDeltagere}</span>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openBulkModalForSprogcenter(sprogcenterObj);
                                        }}
                                        className="add-hold-to-group-button"
                                        title={`Tilføj hold til ${sprogcenterNavn}`}
                                        disabled={!sprogcenterObj || activeTermin.er_afsluttet}
                                      >
                                        + Tilføj Hold
                                      </button>
                                    </span>
                                  </div>
                                </td>
                              </tr>
                              {erExpanded && gruppe.hold.map(h => (
                                <tr key={`mundtlig-${h.id}`} className={h.låst ? 'row-locked' : ''}>
                                  <td>{h.prøve}</td>
                                  <td>{h.deltagere}</td>
                                  <td className={h.låst ? 'status-locked' : 'status-unlocked'}>{h.låst ? <strong>Ja</strong> : 'Nej'}</td>
                                  <td>
                                    {h.mundtlig_censor ? `${h.mundtlig_censor.fornavn} ${h.mundtlig_censor.efternavn}` : 'N/A'}
                                  </td>
                                  <td className="action-cell">
                                    <a href={`${process.env.REACT_APP_API_URL}/api/hold/${h.id}/pdf`} className="pdf-button" title={`Generer PDF for hold ${h.id}`} target="_blank" rel="noopener noreferrer">PDF</a>
                                    <button onClick={() => openHoldModal(h)} className="edit-button icon-button" title={`Rediger hold ${h.id}`} disabled={activeTermin.er_afsluttet}><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M21.731 2.269a2.625 2.625 0 00-3.712 0l-1.157 1.157 3.712 3.712 1.157-1.157a2.625 2.625 0 000-3.712zM19.513 8.199l-3.712-3.712-8.4 8.4a5.25 5.25 0 00-1.32 2.214l-.8 2.685a.75.75 0 00.933.933l2.685-.8a5.25 5.25 0 002.214-1.32l8.4-8.4z" /><path d="M5.25 5.25a3 3 0 00-3 3v10.5a3 3 0 003 3h10.5a3 3 0 003-3V13.5a.75.75 0 00-1.5 0v5.25a1.5 1.5 0 01-1.5 1.5H5.25a1.5 1.5 0 01-1.5-1.5V8.25a1.5 1.5 0 011.5-1.5h5.25a.75.75 0 000-1.5H5.25z" /></svg></button>
                                    <button onClick={() => handleToggleLock(h.id)} className="icon-button" title={h.låst ? `Aflås` : `Lås`} disabled={activeTermin.er_afsluttet}>{h.låst ? (<svg viewBox="0 0 24 24" fill="currentColor" className="lock-icon locked"><path fillRule="evenodd" d="M12 1.5a5.25 5.25 0 00-5.25 5.25v3a3 3 0 00-3 3v6.75a3 3 0 003 3h10.5a3 3 0 003-3v-6.75a3 3 0 00-3-3v-3A5.25 5.25 0 0012 1.5zm-3.75 5.25v3h7.5v-3a3.75 3.75 0 00-7.5 0z" clipRule="evenodd" /></svg>) : (<svg viewBox="0 0 24 24" fill="currentColor" className="lock-icon unlocked"><path d="M18 1.5c2.9 0 5.25 2.35 5.25 5.25v3.75a.75.75 0 01-1.5 0V6.75A3.75 3.75 0 0018 3a3.75 3.75 0 00-3.75 3.75V9.75a.75.75 0 01-1.5 0V6.75C12.75 3.85 15.1 1.5 18 1.5z" /><path fillRule="evenodd" d="M6 6.75V9.75a.75.75 0 01-1.5 0V6.75a.75.75 0 01.75-.75H5.5a5.251 5.251 0 014.66-4.993.75.75 0 11.338 1.46A3.75 3.75 0 005.5 6h-.75a.75.75 0 01-.75.75zM4.5 9.75A3 3 0 001.5 12.75v6.75a3 3 0 003 3h10.5a3 3 0 003-3v-6.75a3 3 0 00-3-3H4.5z" clipRule="evenodd" /></svg>)}</button>
                                    <button onClick={() => handleDeleteHold(h.id)} className="delete-button icon-button" title={`Slet`} disabled={activeTermin.er_afsluttet}><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="delete-icon"><path fillRule="evenodd" d="M16.5 4.478v.227a48.816 48.816 0 013.878.512.75.75 0 11-.256 1.478l-.209-.035-1.005 13.07a3 3 0 01-2.991 2.77H8.084a3 3 0 01-2.991-2.77L4.087 6.66l-.209.035a.75.75 0 01-.256-1.478A48.567 48.567 0 017.5 4.705v-.227c0-1.564 1.213-2.9 2.816-2.9h1.368c1.603 0 2.816 1.336 2.816 2.9zm-1.452.721a.75.75 0 01.216-.534l.018-.018a.75.75 0 01.706-.216L17.5 5.25a.75.75 0 01.216.534l-.018.018a.75.75 0 01-.706.216l-1.281-.214a.75.75 0 01-.216-.534z" clipRule="evenodd" /></svg></button>
                                  </td>
                                </tr>
                              ))}
                              {erExpanded && gruppe.hold.length === 0 && (
                                <tr>
                                  <td colSpan="6" className="no-hold-in-group-message">Ingen hold oprettet for dette sprogcenter.</td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Modal til visning af fordelingslog */}
      <Modal
        isOpen={allocationLogModalOpen}
        onRequestClose={() => setAllocationLogModalOpen(false)}
        contentLabel="Fordelingslog"
        className="ReactModal__Content"
        overlayClassName="ReactModal__Overlay"
        style={{
          content: {
            width: '95vw',
            maxWidth: '1500px'
          }
        }}
      >
        <div>
          <h3>Fordelingslog for {activeTermin ? activeTermin.navn : ''}</h3>
          {allocationLog ? (
            <>
              <p>
                Total hold: {allocationLog.totalHold} •
                {' '}Uden skriftlig censor: <span style={{ color: 'red' }}>{allocationLog.totalSkriftligUdenCensor}</span>
                {' '}• Uden mundtlig censor (blandt mundtlige): <span style={{ color: 'red' }}>{allocationLog.totalMundtligUdenCensor}</span>
              </p>
              <div className="table-container" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                <table className="censor-table">
                  <thead>
                    <tr>
                      <th>Hold ID</th>
                      <th>Prøve</th>
                      <th>Delt.</th>
                      <th>Skriftlig censor</th>
                      <th>Mundtlig censor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allocationLog.holds.map((h) => (
                      <tr key={h.holdId}>
                        <td>{h.holdId}</td>
                        <td>{h.prøve}</td>
                        <td>{h.deltagere}</td>
                        <td>
                          {h.skriftlig.assigned ? (
                            `${h.skriftlig.censorNavn || `ID ${h.skriftlig.censorId}`}`
                          ) : (
                            <span style={{ color: 'red', fontWeight: 'bold' }}>
                              Ingen tildelt – {h.skriftlig.reason || 'ukendt årsag'}
                            </span>
                          )}
                        </td>
                        <td>
                          {h.mundtlig.assigned ? (
                            `${h.mundtlig.censorNavn || `ID ${h.mundtlig.censorId}`}`
                          ) : (
                            h.prøve === 'SP' || h.prøve === 'PD1' || h.prøve === 'PD2' || h.prøve === 'PD3' ? (
                              <span
                                style={{
                                  color: h.mundtlig.reason === 'Holdet blev ikke udvalgt til mundtlig tildeling i denne kørsel.' ? 'inherit' : 'red',
                                  fontWeight: h.mundtlig.reason === 'Holdet blev ikke udvalgt til mundtlig tildeling i denne kørsel.' ? 'normal' : 'bold'
                                }}
                              >
                                {h.mundtlig.reason
                                  ? `Ingen mundtlig censor – ${h.mundtlig.reason}`
                                  : 'Ingen mundtlig censor – ukendt årsag'}
                              </span>
                            ) : (
                              '-'
                            )
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <p>Ingen log tilgængelig.</p>
          )}
          <div className="modal-actions" style={{ marginTop: '15px' }}>
            <button type="button" onClick={() => setAllocationLogModalOpen(false)} className="cancel-button">
              Luk
            </button>
          </div>
        </div>
      </Modal>

      {/* --- MODALER --- */}
      <Modal isOpen={terminModalIsOpen} onRequestClose={() => setTerminModalIsOpen(false)} contentLabel="Tilføj Ny Termin" className="ReactModal__Content" overlayClassName="ReactModal__Overlay">
        <form onSubmit={handleTerminSubmit} className="modal-form">
          <h3>Tilføj Ny Termin</h3>
          {terminModalError && <p className="error-message">{terminModalError}</p>}
          <div className="form-field">
            <label htmlFor="nyTerminNavn">Termin Navn:*</label>
            <input type="text" id="nyTerminNavn" value={nyTerminNavn} onChange={(e) => setNyTerminNavn(e.target.value)} placeholder="f.eks. 2025-Sommer" required />
          </div>
          <div className="modal-actions">
            <button type="submit">Opret Termin</button>
            <button type="button" onClick={() => setTerminModalIsOpen(false)} className="cancel-button">Annuller</button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={holdModalIsOpen} onRequestClose={closeHoldModal} contentLabel="Rediger Hold" className="ReactModal__Content" overlayClassName="ReactModal__Overlay">
        <form onSubmit={handleHoldFormSubmit} className="modal-form">
          <h3>{editingHoldId ? `Rediger Hold ID: ${editingHoldId}` : 'Tilføj Nyt Hold'}</h3>
          {modalError && <p className="error-message" style={{marginBottom: '15px'}}>{modalError}</p>}
          <div className="form-row">
            <div className="form-field"><label htmlFor="deltagereModal">Antal Deltagere:*</label><input type="number" id="deltagereModal" name="deltagere" value={holdFormState.deltagere} onChange={handleHoldFormInputChange} required min="0"/></div>
            <div className="form-field"><label htmlFor="prøveModal">Prøve:*</label><select id="prøveModal" name="prøve" value={holdFormState.prøve} onChange={handleHoldFormInputChange} required><option value="">-- Vælg Prøve --</option><option value="PD1">PD1</option><option value="PD2">PD2</option><option value="PD3">PD3</option><option value="SP">SP (Studieprøven)</option></select></div>
          </div>
          <div className="form-field">
              <label htmlFor="sprogcenter_idModal">Sprogcenter:</label>
              <select id="sprogcenter_idModal" name="sprogcenter_id" value={holdFormState.sprogcenter_id} onChange={handleHoldFormInputChange} disabled={loadingModalOptions}>
                  <option value="">-- Vælg Sprogcenter (valgfrit) --</option>
                  {loadingModalOptions ? <option disabled>Henter...</option> : sprogcentreOptions.map(sc => (<option key={sc.id} value={sc.id}>{sc.navn} (ID: {sc.id})</option>))}
              </select>
          </div>
          <div className="form-row">
            <div className="form-field">
              <label htmlFor="skriftlig_censor_idModal">Skriftlig Censor:</label>
              <select id="skriftlig_censor_idModal" name="skriftlig_censor_id" value={holdFormState.skriftlig_censor_id} onChange={handleHoldFormInputChange} disabled={loadingModalOptions}>
                <option value="">-- Vælg Censor (valgfrit) --</option>
                {censorerOptions.map(censor => (<option key={`sc1-${censor.id}`} value={censor.id}>{censor.fornavn} {censor.efternavn}</option>))}
              </select>
            </div>
          </div>
          <div className="form-field">
            <label htmlFor="mundtlig_censor_idModal">Mundtlig Censor:</label>
            <select id="mundtlig_censor_idModal" name="mundtlig_censor_id" value={holdFormState.mundtlig_censor_id} onChange={handleHoldFormInputChange} disabled={loadingModalOptions}>
              <option value="">-- Vælg Censor (valgfrit) --</option>
              {censorerOptions.map(censor => (<option key={`mc-${censor.id}`} value={censor.id}>{censor.fornavn} {censor.efternavn}</option>))}
            </select>
          </div>
          
          <div className="form-field-check" style={{alignSelf:'flex-end', paddingBottom:'10px'}}><input type="checkbox" id="låstModal" name="låst" checked={holdFormState.låst} onChange={handleHoldFormInputChange}/><label htmlFor="låstModal"> Låst</label></div>
          <div className="modal-actions"><button type="submit" disabled={loadingModalOptions}> {loadingModalOptions ? 'Vent...' : (editingHoldId ? 'Gem Ændringer' : 'Opret Hold')} </button><button type="button" onClick={closeHoldModal} className="cancel-button">Annuller</button></div>
        </form>
      </Modal>

      <BulkCreateHoldModal 
        isOpen={bulkModalIsOpen}
        onRequestClose={() => setBulkModalIsOpen(false)}
        sprogcentre={sprogcentreOptions}
        terminer={terminer}
        aktivTerminId={activeTermin ? activeTermin.id : null}
        sprogcenter={sprogcenterForModal}
        onBulkCreateSuccess={handleBulkCreateSuccess}
      />
    </div>
  );
}

export default HoldListe;